<!-- src/routes/AdminCronJobs.svelte -->
<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { t } from '../i18nStore';
  import { Link } from 'svelte-routing';

  let activeTab = 'cron'; // 'cron' or 'recurring'

  // Cron Jobs State
  let cronJobs = [];
  let isCronLoading = false;
  let cronError = '';
  let isSyncing = false;
  let syncSuccessMsg = '';
  let runningJobId = null;

  // Recurring Promotions State
  let recurringPromos = [];
  let isRecurringLoading = false;
  let recurringError = '';
  let rollingOverId = null;

  // Modal State for Editing Recurrence Rule
  let showEditModal = false;
  let editingPromo = null;
  let editRuleEnabled = true;
  let editRuleFrequency = 'weekly';
  let editRuleEndDate = '';
  let editRuleLeadDays = 2;
  let editRuleAutoPublish = true;
  let editRuleNamingPattern = '';
  let isSavingRule = false;
  let modalError = '';

  // Attach Rule to Promo State
  let showAttachModal = false;
  let attachPromoId = '';
  let attachError = '';
  let isAttaching = false;

  onMount(() => {
    loadCronJobs();
    loadRecurringPromotions();
  });

  async function loadCronJobs() {
    isCronLoading = true;
    cronError = '';
    try {
      const res = await authFetch(getApiUrl('/admin/cron-jobs'));
      if (!res.ok) throw new Error('Failed to load system cron jobs');
      const data = await res.json();
      cronJobs = data.jobs || [];
    } catch (err) {
      cronError = err.message;
    } finally {
      isCronLoading = false;
    }
  }

  async function loadRecurringPromotions() {
    isRecurringLoading = true;
    recurringError = '';
    try {
      const res = await authFetch(getApiUrl('/admin/promotions/recurring'));
      if (!res.ok) throw new Error('Failed to load recurring promotions');
      const data = await res.json();
      recurringPromos = data.data || [];
    } catch (err) {
      recurringError = err.message;
    } finally {
      isRecurringLoading = false;
    }
  }

  async function handleSyncAll() {
    isSyncing = true;
    syncSuccessMsg = '';
    cronError = '';
    try {
      const res = await authFetch(getApiUrl('/admin/cron-jobs/sync'), {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to sync cron jobs');
      syncSuccessMsg = data.message || 'All enabled jobs re-registered in memory!';
      await loadCronJobs();
    } catch (err) {
      cronError = err.message;
    } finally {
      isSyncing = false;
    }
  }

  async function handleToggleJob(job) {
    try {
      const res = await authFetch(getApiUrl(`/admin/cron-jobs/${job.id}/toggle`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !job.enabled })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to toggle job');
      await loadCronJobs();
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleRunNow(jobId) {
    if (!confirm(`Run cron job "${jobId}" immediately now in the background?`)) return;
    runningJobId = jobId;
    try {
      const res = await authFetch(getApiUrl(`/admin/cron-jobs/${jobId}/run`), {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to run job');
      alert(`Job "${jobId}" triggered successfully! Check logs or status below.`);
      setTimeout(loadCronJobs, 2000);
    } catch (err) {
      alert(err.message);
    } finally {
      runningJobId = null;
    }
  }

  async function handleEditSchedule(job) {
    const newFreq = prompt(`Enter standard cron expression or descriptor (@daily, @weekly, 0 2 * * *):`, job.frequency);
    if (!newFreq || newFreq === job.frequency) return;

    try {
      const res = await authFetch(getApiUrl(`/admin/cron-jobs/${job.id}/schedule`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ frequency: newFreq.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update frequency');
      await loadCronJobs();
    } catch (err) {
      alert(err.message);
    }
  }

  // --- RECURRENCE RULES ACTIONS ---

  function openEditModal(promo) {
    editingPromo = promo;
    const rule = promo.recurrenceRule || {};
    editRuleEnabled = rule.enabled !== undefined ? rule.enabled : true;
    editRuleFrequency = rule.frequency || 'weekly';
    editRuleEndDate = rule.endDate ? rule.endDate.split('T')[0] : '';
    editRuleLeadDays = rule.leadDays || 2;
    editRuleAutoPublish = rule.autoPublish !== undefined ? rule.autoPublish : true;
    editRuleNamingPattern = rule.namingPattern || '';
    modalError = '';
    showEditModal = true;
  }

  async function handleSaveRecurrenceRule() {
    if (!editingPromo) return;
    isSavingRule = true;
    modalError = '';

    const payload = {
      enabled: editRuleEnabled,
      frequency: editRuleFrequency,
      leadDays: Number(editRuleLeadDays) || 2,
      autoPublish: editRuleAutoPublish,
      namingPattern: editRuleNamingPattern.trim()
    };

    if (editRuleEndDate) {
      payload.endDate = new Date(editRuleEndDate + 'T23:59:59Z').toISOString();
    }

    try {
      const res = await authFetch(getApiUrl(`/admin/promotions/${editingPromo._id}/recurrence`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update recurrence rule');
      showEditModal = false;
      await loadRecurringPromotions();
    } catch (err) {
      modalError = err.message;
    } finally {
      isSavingRule = false;
    }
  }

  async function handleRollOverNow(promo) {
    if (!confirm(`Are you sure you want to generate the next cycle for "${promo.name}" immediately?`)) return;
    rollingOverId = promo._id;
    try {
      const res = await authFetch(getApiUrl(`/admin/promotions/${promo._id}/recurrence/trigger`), {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to roll over promotion');
      alert(`Success! Generated next promotion: "${data.newPromotion?.name}"`);
      await loadRecurringPromotions();
    } catch (err) {
      alert(err.message);
    } finally {
      rollingOverId = null;
    }
  }

  async function handleAttachRule() {
    if (!attachPromoId.trim()) return;
    isAttaching = true;
    attachError = '';
    try {
      const payload = {
        enabled: true,
        frequency: 'weekly',
        leadDays: 2,
        autoPublish: true
      };
      const res = await authFetch(getApiUrl(`/admin/promotions/${attachPromoId.trim()}/recurrence`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to attach recurrence rule');
      showAttachModal = false;
      attachPromoId = '';
      await loadRecurringPromotions();
    } catch (err) {
      attachError = err.message;
    } finally {
      isAttaching = false;
    }
  }

  function formatDate(dStr) {
    if (!dStr) return 'N/A';
    try {
      const d = new Date(dStr);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return dStr;
    }
  }

  function formatShortDate(dStr) {
    if (!dStr) return 'No End Date (Continuous)';
    try {
      return new Date(dStr).toISOString().split('T')[0];
    } catch (e) {
      return dStr;
    }
  }

  function isRecurrenceExpired(rule) {
    if (!rule || !rule.endDate) return false;
    return new Date(rule.endDate) < new Date();
  }
</script>

<div class="admin-page">
  <div class="header-section">
    <div>
      <h1>{$t('admin.cron_jobs')}</h1>
      <p class="subtitle">System background scheduler, Azure container lifecycle sync, and recurring promotion rules.</p>
    </div>

    <div class="header-actions">
      <button class="btn btn-sync" on:click={handleSyncAll} disabled={isSyncing}>
        {#if isSyncing}
          🔄 Syncing Schedulers...
        {:else}
          🔄 Sync & Re-register All Jobs
        {/if}
      </button>
    </div>
  </div>

  <!-- Azure Container Restart Notice -->
  <div class="callout-box">
    <div class="callout-icon">💡</div>
    <div class="callout-body">
      <strong>Azure Web App Restart & Cold Boot Recovery</strong>
      <p>
        When the Azure Web App container recycles or restarts, in-memory cron entries can drop. 
        Click <strong>"Sync & Re-register All Jobs"</strong> to verify and register all enabled jobs into the live scheduler.
      </p>
      {#if syncSuccessMsg}
        <div class="alert-success">{syncSuccessMsg}</div>
      {/if}
    </div>
  </div>

  <!-- Tab Navigation -->
  <div class="tabs">
    <button 
      class="tab-btn {activeTab === 'cron' ? 'active' : ''}" 
      on:click={() => activeTab = 'cron'}
    >
      ⚙️ System Cron Schedulers ({cronJobs.length})
    </button>
    <button 
      class="tab-btn {activeTab === 'recurring' ? 'active' : ''}" 
      on:click={() => activeTab = 'recurring'}
    >
      🔁 Recurring Promotions ({recurringPromos.length})
    </button>
  </div>

  <!-- TAB 1: SYSTEM CRON JOBS -->
  {#if activeTab === 'cron'}
    <div class="section-card">
      <div class="section-header">
        <h2>Registered System Schedulers</h2>
        <button class="btn btn-secondary btn-sm" on:click={loadCronJobs} disabled={isCronLoading}>Refresh</button>
      </div>

      {#if isCronLoading}
        <div class="loading">Loading cron jobs status...</div>
      {:else if cronError}
        <div class="alert-error">{cronError}</div>
      {:else}
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Job Name / Identifier</th>
                <th>Scheduler Status</th>
                <th>DB Setting</th>
                <th>Frequency</th>
                <th>Next Run Time</th>
                <th>Last Run & Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {#each cronJobs as job}
                <tr>
                  <td>
                    <strong>{job.name}</strong>
                    <div class="job-id"><code>{job.id}</code></div>
                    <div class="job-desc">{job.description}</div>
                  </td>
                  <td>
                    {#if job.isRegistered}
                      <span class="badge badge-active">🟢 In Scheduler (#{job.entryId})</span>
                    {:else if job.enabled}
                      <span class="badge badge-warning">⚠️ Enabled in DB but Not in Memory</span>
                    {:else}
                      <span class="badge badge-inactive">⚪ Unregistered (Disabled)</span>
                    {/if}
                  </td>
                  <td>
                    <button 
                      class="toggle-btn {job.enabled ? 'enabled' : 'disabled'}"
                      on:click={() => handleToggleJob(job)}
                    >
                      {job.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td>
                    <div class="freq-box">
                      <code>{job.frequency}</code>
                      <button class="btn-text" on:click={() => handleEditSchedule(job)} title="Edit frequency">✏️</button>
                    </div>
                  </td>
                  <td>
                    <div class="time-text">{formatDate(job.nextRun)}</div>
                  </td>
                  <td>
                    {#if job.lastRun}
                      <div class="time-text">{formatDate(job.lastRun)}</div>
                      <span class="badge badge-{job.lastStatus === 'success' ? 'active' : job.lastStatus === 'running' ? 'warning' : 'error'}">
                        {job.lastStatus || 'idle'}
                      </span>
                      {#if job.lastError}
                        <div class="error-snippet" title={job.lastError}>{job.lastError}</div>
                      {/if}
                    {:else}
                      <span class="text-muted">Never Run</span>
                    {/if}
                  </td>
                  <td>
                    <button 
                      class="btn btn-run btn-sm" 
                      on:click={() => handleRunNow(job.id)}
                      disabled={runningJobId === job.id}
                    >
                      {runningJobId === job.id ? 'Running...' : '▶ Run Now'}
                    </button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </div>
  {/if}

  <!-- TAB 2: RECURRING PROMOTIONS -->
  {#if activeTab === 'recurring'}
    <div class="section-card">
      <div class="section-header">
        <div>
          <h2>Recurring Promotion Rules</h2>
          <p class="section-sub">Configure frequency, campaign termination (End Date), rollover thresholds, and auto-publishing.</p>
        </div>
        <div class="header-btns">
          <button class="btn btn-primary btn-sm" on:click={() => showAttachModal = true}>+ Attach Rule to Promo ID</button>
          <button class="btn btn-secondary btn-sm" on:click={loadRecurringPromotions} disabled={isRecurringLoading}>Refresh</button>
        </div>
      </div>

      {#if isRecurringLoading}
        <div class="loading">Loading recurring promotions...</div>
      {:else if recurringError}
        <div class="alert-error">{recurringError}</div>
      {:else if recurringPromos.length === 0}
        <div class="empty-state">
          <p>No recurring promotions configured yet.</p>
          <p class="text-muted">You can configure recurrence rules on any existing promotion using the button above or on the promotion's edit page.</p>
        </div>
      {:else}
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Promotion</th>
                <th>Brand</th>
                <th>Frequency</th>
                <th>Current Period</th>
                <th>Recurrence End Date</th>
                <th>Rule Settings</th>
                <th>Last Rollover</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {#each recurringPromos as promo}
                {@const rule = promo.recurrenceRule || {}}
                {@const expired = isRecurrenceExpired(rule)}
                <tr>
                  <td>
                    <Link to={`/promotions/detail/${promo._id}`} class="promo-title">
                      {promo.name}
                    </Link>
                    <div class="promo-meta">
                      <span class="id-tag">{promo._id}</span>
                      {#if promo.parentId}
                        <span class="parent-tag" title="Parent promotion ID">Chained from: {promo.parentId.slice(-6)}</span>
                      {/if}
                    </div>
                  </td>
                  <td>
                    <strong>{promo.brand || 'N/A'}</strong>
                  </td>
                  <td>
                    <span class="badge badge-freq">{rule.frequency || 'weekly'}</span>
                  </td>
                  <td>
                    <div class="date-range">
                      <span>{formatShortDate(promo.startDate)}</span>
                      <span class="arrow">→</span>
                      <span>{formatShortDate(promo.endDate)}</span>
                    </div>
                  </td>
                  <td>
                    {#if rule.endDate}
                      <div class="end-date-cell">
                        <strong>{formatShortDate(rule.endDate)}</strong>
                        {#if expired}
                          <span class="badge badge-error">Expired</span>
                        {:else}
                          <span class="badge badge-active">Active</span>
                        {/if}
                      </div>
                    {:else}
                      <span class="text-muted">Continuous (No End Date)</span>
                    {/if}
                  </td>
                  <td>
                    <div class="rule-pills">
                      <span class="pill">Lead: {rule.leadDays || 2}d</span>
                      <span class="pill">{rule.autoPublish ? 'Auto-Publish' : 'Draft'}</span>
                      <span class="pill {rule.enabled ? 'pill-on' : 'pill-off'}">{rule.enabled ? 'Rule On' : 'Rule Off'}</span>
                    </div>
                  </td>
                  <td>
                    {#if rule.lastGeneratedAt}
                      <span class="time-text">{formatDate(rule.lastGeneratedAt)}</span>
                    {:else}
                      <span class="text-muted">Not Yet Rolled</span>
                    {/if}
                  </td>
                  <td>
                    <div class="action-cell">
                      <button class="btn btn-secondary btn-sm" on:click={() => openEditModal(promo)}>
                        Edit Rule
                      </button>
                      <button 
                        class="btn btn-rollover btn-sm" 
                        on:click={() => handleRollOverNow(promo)}
                        disabled={rollingOverId === promo._id || expired}
                        title="Generate next cycle immediately"
                      >
                        {rollingOverId === promo._id ? 'Generating...' : 'Roll Over Now'}
                      </button>
                    </div>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </div>
  {/if}
</div>

<!-- MODAL: EDIT RECURRENCE RULE -->
{#if showEditModal && editingPromo}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div class="modal-backdrop" on:click|self={() => showEditModal = false}>
    <div class="modal-card">
      <div class="modal-header">
        <h3>Edit Recurrence Rule</h3>
        <button class="close-x" on:click={() => showEditModal = false}>&times;</button>
      </div>

      <div class="modal-body">
        <p class="target-title">Target: <strong>{editingPromo.name}</strong></p>

        {#if modalError}
          <div class="alert-error">{modalError}</div>
        {/if}

        <div class="form-group">
          <label class="checkbox-label">
            <input type="checkbox" bind:checked={editRuleEnabled} />
            <span>Enable Recurrence Rule</span>
          </label>
        </div>

        <div class="form-grid">
          <div class="form-group">
            <label for="editRuleFreq">Recurrence Frequency</label>
            <select id="editRuleFreq" bind:value={editRuleFrequency}>
              <option value="weekly">Weekly (+7 Days)</option>
              <option value="monthly">Monthly (Calendar Month)</option>
              <option value="biweekly">Bi-weekly (+14 Days)</option>
            </select>
          </div>

          <div class="form-group">
            <label for="editLeadDays">Lead Days (Rollover Trigger)</label>
            <input id="editLeadDays" type="number" min="1" max="14" bind:value={editRuleLeadDays} />
            <small class="hint">Days before promotion ends to create the next cycle (Default: 2).</small>
          </div>
        </div>

        <div class="form-group highlight-group">
          <label for="editEndDate">
            <strong>Recurrence End Date (Campaign Cutoff)</strong>
          </label>
          <input id="editEndDate" type="date" bind:value={editRuleEndDate} />
          <small class="hint">The recurrence engine will stop generating new cycles after this date. Leave blank for indefinite recurrence.</small>
        </div>

        <div class="form-group">
          <label class="checkbox-label">
            <input type="checkbox" bind:checked={editRuleAutoPublish} />
            <span>Auto-Publish new cycles as Verified (uncheck to save as Pending Review)</span>
          </label>
        </div>

        <div class="form-group">
          <label for="editNamingPattern">Custom Naming Pattern (Optional)</label>
          <input 
            id="editNamingPattern" 
            type="text" 
            placeholder="e.g. 大家樂 樂賞錢 - &#123;&#123;startDate:D/M/YYYY&#125;&#125;" 
            bind:value={editRuleNamingPattern} 
          />
          <small class="hint">Placeholders: <code>{'{'}{'{'}startDate:D/M/YYYY{'}'}{'}'}</code>, <code>{'{'}{'{'}startDate:YYYY年M月{'}'}{'}'}</code>. If blank, smart regex auto-increments the existing dates.</small>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary" on:click={() => showEditModal = false}>Cancel</button>
        <button class="btn btn-primary" on:click={handleSaveRecurrenceRule} disabled={isSavingRule}>
          {isSavingRule ? 'Saving...' : 'Save Recurrence Rule'}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- MODAL: ATTACH RULE TO PROMOTION -->
{#if showAttachModal}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div class="modal-backdrop" on:click|self={() => showAttachModal = false}>
    <div class="modal-card">
      <div class="modal-header">
        <h3>Attach Recurrence Rule to Promotion</h3>
        <button class="close-x" on:click={() => showAttachModal = false}>&times;</button>
      </div>

      <div class="modal-body">
        <p>Enter the 24-character hexadecimal MongoDB ID of the promotion to attach recurrence to:</p>
        {#if attachError}
          <div class="alert-error">{attachError}</div>
        {/if}
        <div class="form-group">
          <label for="attachId">Promotion ID</label>
          <input id="attachId" type="text" placeholder="e.g. 6ac5c257baa1e9dfc66b7118" bind:value={attachPromoId} />
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-secondary" on:click={() => showAttachModal = false}>Cancel</button>
        <button class="btn btn-primary" on:click={handleAttachRule} disabled={isAttaching || !attachPromoId.trim()}>
          {isAttaching ? 'Attaching...' : 'Attach Rule'}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .admin-page {
    max-width: 1200px;
    margin: 2rem auto;
    padding: 0 1rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  .header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  h1 { margin: 0; font-size: 1.8rem; color: #1f2937; }
  .subtitle { margin: 0.25rem 0 0; color: #6b7280; font-size: 0.95rem; }

  /* Callout box */
  .callout-box {
    display: flex;
    gap: 1rem;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 1rem 1.25rem;
    margin-bottom: 1.5rem;
    color: #1e40af;
  }
  .callout-icon { font-size: 1.5rem; }
  .callout-body p { margin: 0.25rem 0 0; font-size: 0.9rem; color: #1e3a8a; }

  /* Tabs */
  .tabs {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    border-bottom: 2px solid #e5e7eb;
  }
  .tab-btn {
    padding: 0.75rem 1.25rem;
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    font-size: 1rem;
    font-weight: 600;
    color: #6b7280;
    cursor: pointer;
    margin-bottom: -2px;
  }
  .tab-btn.active {
    color: #2563eb;
    border-bottom-color: #2563eb;
  }

  /* Section Card */
  .section-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
  .section-header h2 { margin: 0; font-size: 1.3rem; color: #111827; }
  .section-sub { margin: 0.25rem 0 0; color: #6b7280; font-size: 0.85rem; }

  /* Table styling */
  .table-container { overflow-x: auto; }
  .data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
    text-align: left;
  }
  .data-table th {
    background: #f9fafb;
    padding: 0.75rem 1rem;
    font-weight: 600;
    color: #4b5563;
    border-bottom: 2px solid #e5e7eb;
  }
  .data-table td {
    padding: 0.85rem 1rem;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;
  }
  .data-table tr:hover { background: #f9fafb; }

  .job-id code { background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-size: 0.8rem; color: #4b5563; }
  .job-desc { font-size: 0.8rem; color: #6b7280; margin-top: 4px; max-width: 320px; }
  .time-text { font-size: 0.82rem; color: #374151; }

  /* Badges */
  .badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .badge-active { background: #dcfce7; color: #15803d; }
  .badge-inactive { background: #f3f4f6; color: #6b7280; }
  .badge-warning { background: #fef9c3; color: #a16207; }
  .badge-error { background: #fee2e2; color: #b91c1c; }
  .badge-freq { background: #e0e7ff; color: #4338ca; font-family: monospace; text-transform: uppercase; }

  /* Toggle button */
  .toggle-btn {
    border: none;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
  }
  .toggle-btn.enabled { background: #dcfce7; color: #166534; }
  .toggle-btn.disabled { background: #fee2e2; color: #991b1b; }

  /* Buttons */
  .btn {
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: background 0.15s;
  }
  .btn-sm { padding: 0.35rem 0.75rem; font-size: 0.8rem; }
  .btn-primary { background: #2563eb; color: white; }
  .btn-primary:hover { background: #1d4ed8; }
  .btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #d1d5db; }
  .btn-secondary:hover { background: #e5e7eb; }
  .btn-sync { background: #059669; color: white; font-weight: 600; }
  .btn-sync:hover { background: #047857; }
  .btn-run { background: #4f46e5; color: white; }
  .btn-run:hover { background: #4338ca; }
  .btn-rollover { background: #0284c7; color: white; }
  .btn-rollover:hover { background: #0369a1; }
  .btn-text { background: none; border: none; cursor: pointer; padding: 2px 4px; }
  .btn:disabled { opacity: 0.6; cursor: not-allowed; }

  /* Promo table items */
  .promo-title { font-weight: 600; color: #1d4ed8; text-decoration: none; }
  .promo-title:hover { text-decoration: underline; }
  .promo-meta { display: flex; gap: 0.5rem; align-items: center; margin-top: 4px; }
  .id-tag { font-size: 0.75rem; font-family: monospace; color: #9ca3af; }
  .parent-tag { font-size: 0.75rem; color: #6b7280; background: #f3f4f6; padding: 1px 5px; border-radius: 4px; }
  .date-range { font-size: 0.82rem; color: #374151; display: flex; gap: 4px; align-items: center; }
  .date-range .arrow { color: #9ca3af; }
  .end-date-cell { display: flex; flex-direction: column; gap: 4px; }
  .rule-pills { display: flex; flex-wrap: wrap; gap: 4px; }
  .pill { font-size: 0.75rem; background: #f3f4f6; color: #4b5563; padding: 2px 6px; border-radius: 4px; }
  .pill-on { background: #dcfce7; color: #166534; font-weight: 600; }
  .pill-off { background: #fee2e2; color: #991b1b; }
  .action-cell { display: flex; gap: 6px; }

  /* Alerts */
  .alert-success { background: #dcfce7; color: #15803d; padding: 0.5rem; border-radius: 4px; margin-top: 0.5rem; font-size: 0.85rem; }
  .alert-error { background: #fee2e2; color: #b91c1c; padding: 0.5rem; border-radius: 4px; margin-bottom: 1rem; font-size: 0.85rem; }

  /* Modals */
  .modal-backdrop {
    position: fixed; inset: 0; background: rgba(0,0,0,0.5);
    display: flex; align-items: center; justify-content: center; z-index: 1000;
  }
  .modal-card {
    background: white; border-radius: 8px; width: 100%; max-width: 550px;
    max-height: 90vh; overflow-y: auto; padding: 1.5rem; box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  }
  .modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
  .modal-header h3 { margin: 0; font-size: 1.25rem; }
  .close-x { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: #9ca3af; }
  .target-title { font-size: 0.9rem; color: #374151; margin-bottom: 1rem; }
  .form-group { margin-bottom: 1.1rem; }
  .form-group label { display: block; font-size: 0.85rem; font-weight: 600; margin-bottom: 0.35rem; color: #374151; }
  .form-group input[type="text"],
  .form-group input[type="number"],
  .form-group input[type="date"],
  .form-group select {
    width: 100%; padding: 0.5rem; border: 1px solid #d1d5db; border-radius: 6px; box-sizing: border-box; font-size: 0.9rem;
  }
  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .checkbox-label { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; font-weight: normal !important; }
  .checkbox-label input { width: auto; margin: 0; }
  .hint { display: block; font-size: 0.75rem; color: #6b7280; margin-top: 0.25rem; }
  .highlight-group { background: #fefce8; border: 1px solid #fef08a; padding: 0.75rem; border-radius: 6px; }
  .modal-footer { display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 1.5rem; }
</style>
